import type { DirectoryFolder, DirectoryTree } from "@/api";
import {
  buildDownloadPlan,
  type DownloadEntry,
  type DownloadSelection,
  type DownloadSourceFiles,
} from "./downloadPlan";

/**
 * 저장 위치 미리보기 모델. 최상위 폴더 한 줄과 그 아래 예시 경로 몇 줄로 이루어진다.
 *
 * 폴더 구성과 파일 수는 디렉터리 트리가 제공하고, 예시 경로만 표본에서 고른다. 예시만 나열하면
 * 표본이 전체 범위로 읽힌다. 계획은 `relative_path` 오름차순이므로 앞줄이 항상 알파벳 첫 폴더로
 * 채워진다. 그러면 그 폴더만 받는 것처럼 보인다(ZERG-491).
 */

// 폴더마다 보여 줄 예시 경로 수. 채널이 나뉘는 것을 보이려면 둘은 있어야 한다. 더 늘리면 폴더가
// 다섯인 데이터셋에서 뒤쪽 폴더가 상자 밖으로 밀린다. 그러면 막으려던 오해가 그대로 남는다.
const SAMPLE_PATHS_PER_FOLDER = 2;

export type DownloadPreviewGroup = {
  // 최상위 폴더 이름. 루트 직속 파일 묶음이면 null.
  folder: string | null;
  // 이 폴더의 파일 수(하위 폴더 포함). 표본이 아니라 트리가 센 값이다.
  fileCount: number;
  // 표본에서 고른 예시 경로. 폴더 이름을 뺀 나머지다. 표본에 이 폴더 파일이 없으면 비어 있다.
  samplePaths: string[];
  // 예시로 보여 준 것 외에 더 있는가.
  hasMore: boolean;
};

export type DownloadPreview = {
  // 트리 순서(이름 오름차순). 루트 직속 파일이 있으면 그 묶음이 맨 앞이다.
  groups: DownloadPreviewGroup[];
  // 데이터셋 전체 파일 수. 폴더별 수의 합이므로 화면의 숫자끼리 어긋나지 않는다.
  totalFiles: number;
};

export function buildDownloadPreview(
  tree: DirectoryTree,
  sample: DownloadSourceFiles,
  { includeExcluded }: Pick<DownloadSelection, "includeExcluded">,
): DownloadPreview {
  // 표본도 실제 실행과 같은 계획을 거친다. 정렬과 excluded 판정이 다르면 예시가 틀린다.
  const plan = buildDownloadPlan(sample, { prefix: null, includeExcluded });
  const samples = groupSamplePaths(plan.entries);

  const groups: DownloadPreviewGroup[] = [];
  const rootFileCount = tree.root_canonical_file_count + tree.root_extra_file_count;
  if (rootFileCount > 0) {
    groups.push(previewGroup(null, rootFileCount, samples.get("") ?? []));
  }
  for (const folder of tree.folders) {
    groups.push(previewGroup(folder.name, folderFileCount(folder), samples.get(folder.name) ?? []));
  }

  return {
    groups,
    totalFiles: groups.reduce((sum, group) => sum + group.fileCount, 0),
  };
}

function previewGroup(
  folder: string | null,
  fileCount: number,
  paths: readonly string[],
): DownloadPreviewGroup {
  const samplePaths = pickSamplePaths(paths);
  return { folder, fileCount, samplePaths, hasMore: fileCount > samplePaths.length };
}

// 하위 폴더까지 재귀로 센 값(`DirectoryFolder` 계약). 트리를 다시 순회할 필요가 없다.
function folderFileCount(folder: DirectoryFolder): number {
  return folder.canonical_file_count + folder.extra_file_count;
}

// 최상위 폴더 → 그 아래 경로들(폴더 이름을 뺀 나머지). 루트 직속 파일의 키는 ''.
function groupSamplePaths(entries: readonly DownloadEntry[]): Map<string, string[]> {
  const grouped = new Map<string, string[]>();
  for (const entry of entries) {
    const boundary = entry.relativePath.indexOf("/");
    const folder = boundary < 0 ? "" : entry.relativePath.slice(0, boundary);
    const rest = boundary < 0 ? entry.relativePath : entry.relativePath.slice(boundary + 1);
    const paths = grouped.get(folder);
    if (paths === undefined) {
      grouped.set(folder, [rest]);
      continue;
    }
    paths.push(rest);
  }
  return grouped;
}

/**
 * 예시로 보여 줄 경로를 고른다. **서로 다른 하위 폴더를 먼저** 채운다.
 *
 * 정렬 순서대로 앞에서 자르면 한 채널의 연속된 에피소드로만 채워진다. 이 미리보기는 같은
 * 파일명이 채널 폴더별로 나뉘는 것을 보여 주려고 있다. 그래서 하위 폴더를 먼저 보여 준다.
 */
function pickSamplePaths(paths: readonly string[]): string[] {
  const picked = new Set<string>();
  const seenChildren = new Set<string>();
  for (const path of paths) {
    if (picked.size >= SAMPLE_PATHS_PER_FOLDER) break;
    const child = childSegment(path);
    if (seenChildren.has(child)) continue;
    seenChildren.add(child);
    picked.add(path);
  }
  // 하위 폴더가 그보다 적으면 남은 자리를 순서대로 채운다.
  for (const path of paths) {
    if (picked.size >= SAMPLE_PATHS_PER_FOLDER) break;
    picked.add(path);
  }
  // 입력이 정렬되어 있어도 위 두 단계가 순서를 바꾼다. 트리로 읽히게 다시 정렬한다.
  return [...picked].sort((left, right) => left.localeCompare(right));
}

// 경로의 첫 세그먼트. 하위 폴더 이름이거나, 하위 폴더가 없으면 파일명 자체다.
function childSegment(path: string): string {
  const boundary = path.indexOf("/");
  return boundary < 0 ? path : path.slice(0, boundary);
}
